import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { applyThemeColors, normalizeColorTheme } from '../themes/colors';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Try to get saved theme from localStorage, default to 'system'
    return localStorage.getItem('preferred-theme') || 'system';
  });
  const [colorTheme, setColorTheme] = useState(() => {
    // Removed or invalid saved themes migrate to the current default.
    return normalizeColorTheme(localStorage.getItem('preferred-color-theme'));
  });
  const [glowEnabled, setGlowEnabled] = useState(() => {
    // Try to get saved glow setting from localStorage, default to true
    const saved = localStorage.getItem('glow-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [glowColor, setGlowColor] = useState(() => {
    // Try to get saved glow color from localStorage, default to blue
    const saved = localStorage.getItem('glow-color');
    return saved !== null ? saved : '#3b82f6';
  });

  // Get effective theme (resolves 'system' to actual theme)
  const getEffectiveTheme = useCallback(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  }, [theme]);

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('preferred-theme', theme);
  }, [theme]);

  // Save color theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('preferred-color-theme', colorTheme);
  }, [colorTheme]);

  // Save glow setting to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('glow-enabled', JSON.stringify(glowEnabled));
  }, [glowEnabled]);

  // Save glow color to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('glow-color', glowColor);
  }, [glowColor]);

  // Apply theme changes
  useEffect(() => {
    const root = window.document.documentElement;
    const effectiveTheme = getEffectiveTheme();
    
    // Apply Tailwind classes for existing components
    root.classList.remove('light', 'dark');
    root.classList.add(effectiveTheme);
    
    // Apply our custom color theme
    applyThemeColors(colorTheme, effectiveTheme);

    // Apply glow color as CSS custom property
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const rgb = hexToRgb(glowColor);
    if (rgb) {
      root.style.setProperty('--glow-color', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }

  }, [theme, colorTheme, glowColor, getEffectiveTheme]);

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        const effectiveTheme = mediaQuery.matches ? 'dark' : 'light';
        applyThemeColors(colorTheme, effectiveTheme);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, colorTheme]);

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      colorTheme, 
      setColorTheme,
      glowEnabled,
      setGlowEnabled,
      glowColor,
      setGlowColor,
      effectiveTheme: getEffectiveTheme()
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
